/**
 * RetentionPage — manage data retention policies.
 *
 * Each target type (events, images, sessions, etc.) has a configurable
 * retention period. Rows are vendor-scoped: infra-admin manages the global
 * default set, a vendor-admin manages their own vendor's set, supervisors
 * get read-only access to their vendor's set. Audit-log hard deletion is
 * opt-in (disabled by default) — see the info banner in the edit dialog.
 */

import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Chip, Alert, CircularProgress, Switch,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  MenuItem, Select, FormControl, InputLabel, Snackbar, Tooltip, IconButton, Stack,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import api, { isSupervisor } from '../api/client';
import useTimezone from '../hooks/useTimezone';
import { formatDateTime } from '../utils/format';
import InfoTooltip from '../components/InfoTooltip';

// ---------------------------------------------------------------------------
// API helpers
// ---------------------------------------------------------------------------

const getPolicies = () =>
  api.get('/api/v1/retention').then((r) => r.data);

const updatePolicy = (target, data) =>
  api.put(`/api/v1/retention/${target}`, data).then((r) => r.data);

const triggerRetention = () =>
  api.post('/api/v1/retention/run').then((r) => r.data);

// ---------------------------------------------------------------------------
// EditPolicyDialog
// ---------------------------------------------------------------------------

const TARGET_LABEL = {
  images: 'Uploaded Images / Snapshots',
  events: 'Events (other/legacy)',
  drowsiness_events: 'Drowsiness Events',
  yawning_events: 'Yawning Events',
  distracted_events: 'Distracted Events',
  sessions: 'Operator Sessions',
  breaks: 'Break Sessions',
  audit_logs: 'Audit Logs',
  device_health: 'Device Health',
  metrics: 'Metrics',
  presence: 'Presence Summaries',
  away_sessions: 'Away Sessions / Presence Events',
  notifications: 'Notifications',
  temporary_files: 'Temporary Files',
  redis_cache: 'Redis Cache',
  edge_cached_events: 'Edge Cached Events',
  edge_logs: 'Edge Logs',
  edge_snapshots: 'Edge Snapshots',
  edge_videos: 'Recorded Videos (Edge)',
};

function EditPolicyDialog({ open, onClose, policy, onDone }) {
  const [retentionDays, setRetentionDays] = useState(90);
  const [action, setAction] = useState('delete');
  const [isEnabled, setIsEnabled] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open && policy) {
      setRetentionDays(policy.retention_days);
      setAction(policy.action);
      setIsEnabled(policy.is_enabled);
      setError('');
    }
  }, [open, policy]);

  const handle = async () => {
    const days = parseInt(retentionDays, 10);
    if (!days || days < 1 || days > 3650) {
      setError('Retention days must be between 1 and 3650.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await updatePolicy(policy.target, {
        retention_days: days,
        action,
        is_enabled: isEnabled,
      });
      onDone(`Policy for "${policy.target}" updated.`);
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update policy.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Edit Retention Policy — {policy?.target}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {policy?.target === 'audit_logs' && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Audit logs are the security trail. Enabling this with action "Delete" will
            permanently remove entries older than the retention period — off by default
            for a reason; only enable if your compliance policy allows it.
          </Alert>
        )}
        {policy?.not_yet_implemented && (
          <Alert severity="info" sx={{ mb: 2 }}>
            This target has no dedicated storage yet — the policy is saved for future use
            but cleanup currently no-ops for it.
          </Alert>
        )}
        {policy?.edge_managed && (
          <Alert severity="info" sx={{ mb: 2 }}>
            This data lives on the edge device, not the central server. The policy is saved
            but not enforced centrally until the edge client supports pulling retention config.
          </Alert>
        )}
        <Typography variant="caption" color="text.secondary">
          Retention Days
          <InfoTooltip
            description="Data older than this will be automatically removed."
            scope="existing" saveRequired
          />
        </Typography>
        <TextField
          label="Retention (days)"
          type="number"
          value={retentionDays}
          onChange={(e) => setRetentionDays(e.target.value)}
          fullWidth
          size="small"
          inputProps={{ min: 1, max: 3650 }}
          sx={{ mb: 2, mt: 0.5 }}
        />
        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel>Action</InputLabel>
          <Select value={action} label="Action" onChange={(e) => setAction(e.target.value)}>
            <MenuItem value="delete">Delete</MenuItem>
            <MenuItem value="archive">Archive</MenuItem>
            <MenuItem value="soft_delete">Soft delete</MenuItem>
          </Select>
        </FormControl>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Switch
            checked={isEnabled}
            onChange={(e) => setIsEnabled(e.target.checked)}
            color="primary"
          />
          <Typography variant="body2">
            {isEnabled ? 'Enabled' : 'Disabled'}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={handle} variant="contained" disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// RetentionPage
// ---------------------------------------------------------------------------

const TARGET_DESCRIPTION = {
  images: 'Event snapshot images / uploaded images',
  events: 'Detection events not covered by a specific type below',
  drowsiness_events: 'Drowsiness detection events',
  yawning_events: 'Yawning detection events',
  distracted_events: 'Distraction detection events',
  sessions: 'Operator work sessions',
  breaks: 'Break session records',
  audit_logs: 'Security audit trail — hard delete is opt-in',
  device_health: 'Device health metrics',
  metrics: 'Performance metrics (Prometheus-managed)',
  presence: 'Presence Monitoring shift summaries',
  away_sessions: 'Away-session records within a shift',
  notifications: 'In-app notifications (not yet implemented)',
  temporary_files: 'Server temp/staging files (not yet implemented)',
  redis_cache: 'Redis cache/stream data (not yet implemented)',
  edge_cached_events: 'Edge device local event queue (edge-managed)',
  edge_logs: 'Edge device application logs (edge-managed)',
  edge_snapshots: 'Edge device local snapshot files (edge-managed)',
  edge_videos: 'Edge device recorded video files (edge-managed)',
};

export default function RetentionPage() {
  const readOnly = isSupervisor();
  const tz = useTimezone();
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [editTarget, setEditTarget] = useState(null);
  const [running, setRunning] = useState(false);
  const [toast, setToast] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getPolicies();
      setPolicies(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load retention policies.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleRunNow = async () => {
    if (readOnly) return;
    if (!window.confirm('Trigger retention enforcement now? This will permanently delete expired data.')) return;
    setRunning(true);
    try {
      const result = await triggerRetention();
      const summary = Object.entries(result.deleted || {})
        .map(([k, v]) => `${k}: ${v}`)
        .join(', ') || 'nothing deleted';
      setToast(`Retention run complete — ${summary}`);
      load();
    } catch (err) {
      setError(err.response?.data?.detail || 'Retention run failed.');
    } finally {
      setRunning(false);
    }
  };

  const handleDone = (msg) => {
    setToast(msg);
    load();
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Retention Policies</Typography>
          <Typography variant="body2" color="text.secondary">
            Configure how long each data type is kept · runs daily at 02:00 UTC
            {readOnly ? ' · read-only' : ''}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button startIcon={<RefreshIcon />} onClick={load}>Refresh</Button>
          {!readOnly && (
            <Tooltip title="Immediately enforce all enabled policies">
              <Button
                variant="outlined"
                color="warning"
                startIcon={<PlayArrowIcon />}
                onClick={handleRunNow}
                disabled={running}
              >
                {running ? 'Running…' : 'Run Now'}
              </Button>
            </Tooltip>
          )}
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading && policies.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} elevation={1}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell>Target</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Retention</TableCell>
                <TableCell>Action</TableCell>
                <TableCell>Enabled</TableCell>
                <TableCell>Last Run</TableCell>
                <TableCell align="right">Edit</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {policies.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    No retention policies configured yet.
                  </TableCell>
                </TableRow>
              ) : policies.map((p) => (
                <TableRow key={p.target} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {TARGET_LABEL[p.target] || p.target}
                    </Typography>
                    <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
                      {p.edge_managed && <Chip size="small" variant="outlined" color="info" label="Edge-managed" />}
                      {p.not_yet_implemented && <Chip size="small" variant="outlined" label="Not yet implemented" />}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {TARGET_DESCRIPTION[p.target] || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{p.retention_days} days</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={p.action}
                      size="small"
                      color={p.action === 'delete' ? 'error' : p.action === 'archive' ? 'info' : 'warning'}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={p.is_enabled ? 'Yes' : 'No'}
                      color={p.is_enabled ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontFamily="monospace" fontSize={12} color="text.secondary">
                      {p.last_run_at ? formatDateTime(p.last_run_at, tz) : 'Never'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {readOnly ? (
                      <Typography variant="caption" color="text.secondary">Read-only</Typography>
                    ) : (
                      <Tooltip title="Edit policy">
                        <IconButton size="small" onClick={() => setEditTarget(p)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {editTarget && (
        <EditPolicyDialog
          open
          policy={editTarget}
          onClose={() => setEditTarget(null)}
          onDone={handleDone}
        />
      )}

      <Snackbar
        open={!!toast}
        autoHideDuration={5000}
        onClose={() => setToast('')}
        message={toast}
      />
    </Box>
  );
}
